import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../components/context/AuthProvider";
import { apiFetch } from "../../../services/apiClient";
import {
  Button,
  DataTable,
  LoadingState,
  EmptyState,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Select,
} from "../../../components/ui";
import type { Column } from "../../../components/ui/DataTable";
import { getAvailableThemes } from "../../../config/sessionThemes";
import { sessionService, PersistedChatMessage } from "../../../services/sessionService";

interface User {
  id: number;
  username: string;
  name: string;
  email: string;
}

interface Character {
  id: number;
  name: string;
  campaignId: number;
  userId: number;
}

interface Session extends Record<string, unknown> {
  id: number;
  campaignId: number;
  dmPlayerId: number;
  dmUsername: string;
  dmName: string;
  sessionLog: string;
  status: string; // 'created', 'active', 'paused', 'ended'
  theme?: string; // Theme for the session
  startedAt?: string;
  endedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface Campaign {
  id: number;
  name: string;
  createdBy: string;
  createdByUserId: number;
  users: User[];
}

interface CampaignSessionsProps {
  campaign: Campaign;
  onUpdate?: () => void;
}

interface GetSessionsResponse {
  sessions: Session[];
  total: number;
}

interface CreateSessionRequest {
  campaignId: number;
  dmPlayerId: number;
  theme: string;
}

const CampaignSessions: React.FC<CampaignSessionsProps> = ({ campaign, onUpdate }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const pageSize = 10;

  // Create session modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedDMId, setSelectedDMId] = useState<number | null>(null);
  const [selectedTheme, setSelectedTheme] = useState<string>("dark");
  const [creating, setCreating] = useState(false);

  // Check if current user is campaign owner (created_by field would be needed from backend)
  const [isCampaignOwner, setIsCampaignOwner] = useState(false);

  // Session action states
  const [startingSessions, setStartingSessions] = useState<Set<number>>(new Set());
  const [endingSessions, setEndingSessions] = useState<Set<number>>(new Set());
  const [deletingSessions, setDeletingSessions] = useState<Set<number>>(new Set());
  const [joiningSession, setJoiningSession] = useState<number | null>(null);
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
  const [selectedCharacterId, setSelectedCharacterId] = useState<string | null>(null);
  const [joiningSessionInProgress, setJoiningSessionInProgress] = useState(false);

  // User characters state
  const [userCharacters, setUserCharacters] = useState<Character[]>([]);

  // Session log modal state
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [viewingLogSession, setViewingLogSession] = useState<Session | null>(null);
  const [sessionLogMessages, setSessionLogMessages] = useState<PersistedChatMessage[]>([]);
  const [loadingLog, setLoadingLog] = useState(false);

  const fetchSessions = async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/api/protected/campaigns/${campaign.id}/sessions`, {
        method: "POST",
        body: JSON.stringify({
          page,
          pageSize,
        }),
      });

      if (response.ok) {
        const data: GetSessionsResponse = await response.json();
        setSessions(data.sessions);
        setTotal(data.total);
      } else if (response.status === 403) {
        setError("You don't have permission to view sessions for this campaign.");
      } else {
        setError("Failed to fetch sessions. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching sessions:", error);
      setError("An error occurred while loading sessions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserCharacters = async () => {
    try {
      const response = await apiFetch(
        `/api/protected/campaigns/campaign/${campaign.id}/characters`
      );

      if (response.ok) {
        const characters: Character[] = await response.json();
        // Filter characters owned by the current user
        const currentUserCharacters = characters.filter((char) => char.userId === currentUserId);
        setUserCharacters(currentUserCharacters);
      } else {
        console.error("Failed to fetch user characters");
      }
    } catch (error) {
      console.error("Error fetching user characters:", error);
    }
  };

  const createSession = async () => {
    if (!selectedDMId) {
      return;
    }

    setCreating(true);
    try {
      const response = await apiFetch(`/api/protected/campaigns/${campaign.id}/sessions/create`, {
        method: "POST",
        body: JSON.stringify({
          campaignId: campaign.id,
          dmPlayerId: selectedDMId,
          theme: selectedTheme,
        } as CreateSessionRequest),
      });

      if (response.ok) {
        setIsCreateModalOpen(false);
        setSelectedDMId(null);
        setSelectedTheme("dark");
        fetchSessions(currentPage); // Refresh the list
        if (onUpdate) {
          onUpdate();
        }
      } else if (response.status === 403) {
        setError("Only the campaign owner can create sessions.");
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || "Failed to create session. Please try again.");
      }
    } catch (error) {
      console.error("Error creating session:", error);
      setError("An error occurred while creating the session. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const startSession = async (sessionId: number) => {
    setStartingSessions((prev) => new Set(prev).add(sessionId));
    try {
      const response = await apiFetch(`/api/protected/sessions/${sessionId}/start`, {
        method: "POST",
      });

      if (response.ok) {
        fetchSessions(currentPage); // Refresh the list
        if (onUpdate) {
          onUpdate();
        }
      } else if (response.status === 403) {
        setError("Only the DM can start the session.");
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || "Failed to start session. Please try again.");
      }
    } catch (error) {
      console.error("Error starting session:", error);
      setError("An error occurred while starting the session. Please try again.");
    } finally {
      setStartingSessions((prev) => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  };

  const joinSession = async () => {
    if (!joiningSession || !selectedCharacterId || joiningSessionInProgress) return;

    setJoiningSessionInProgress(true);
    try {
      const response = await apiFetch(`/api/protected/sessions/join`, {
        method: "POST",
        body: JSON.stringify({
          session_id: joiningSession,
          character_id: parseInt(selectedCharacterId),
        }),
      });

      if (response.ok) {
        setIsJoinModalOpen(false);
        setJoiningSession(null);
        setSelectedCharacterId(null);
        setError(null);

        // Navigate to session page regardless of whether user was already participating or just joined
        navigate(`/protected/sessions/${joiningSession}`);
      } else if (response.status === 403) {
        setError("You don't have permission to join this session.");
      } else if (response.status === 400) {
        const errorData = await response.json().catch(() => ({}));
        // Remove the specific handling for "already participating" since backend now handles it properly
        setError(errorData.message || "Failed to join session. Please try again.");
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || "Failed to join session. Please try again.");
      }
    } catch (error) {
      console.error("Error joining session:", error);
      setError("An error occurred while joining the session. Please try again.");
    } finally {
      setJoiningSessionInProgress(false);
    }
  };

  const openJoinModal = (session: Session) => {
    const sessionId = session.id;
    setJoiningSession(sessionId);

    const isDM = session.dmPlayerId === currentUserId;

    // If player has no characters in this campaign, redirect to characters page
    if (userCharacters.length === 0 && !isDM) {
      navigate(`/protected/characters`);
      return;
    } else if (userCharacters.length === 1) {
      // Auto-join with the only character
      joinSessionDirectly(sessionId, userCharacters[0].id);
    } else {
      // Multiple characters — show selection modal
      setIsJoinModalOpen(true);
      setSelectedCharacterId(null);
    }
  };

  const joinSessionDirectly = async (sessionId: number, characterId: number | null) => {
    if (joiningSessionInProgress) return;

    setJoiningSessionInProgress(true);
    try {
      const response = await apiFetch(`/api/protected/sessions/join`, {
        method: "POST",
        body: JSON.stringify({
          session_id: sessionId,
          character_id: characterId,
        }),
      });

      if (response.ok) {
        setError(null);

        // Navigate to session page regardless of whether user was already participating or just joined
        navigate(`/protected/sessions/${sessionId}`);
      } else if (response.status === 403) {
        setError("You don't have permission to join this session.");
      } else if (response.status === 400) {
        const errorData = await response.json().catch(() => ({}));
        // Remove the specific handling for "already participating" since backend now handles it properly
        setError(errorData.message || "Failed to join session. Please try again.");
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || "Failed to join session. Please try again.");
      }
    } catch (error) {
      console.error("Error joining session:", error);
      setError("An error occurred while joining the session. Please try again.");
    } finally {
      setJoiningSessionInProgress(false);
    }
  };

  const endSession = async (sessionId: number) => {
    setEndingSessions((prev) => new Set(prev).add(sessionId));
    try {
      await sessionService.endSession(String(sessionId));
      fetchSessions(currentPage);
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to end session. Please try again.");
    } finally {
      setEndingSessions((prev) => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  };

  const deleteSession = async (sessionId: number) => {
    if (!window.confirm("Are you sure you want to delete this session? This cannot be undone.")) {
      return;
    }
    setDeletingSessions((prev) => new Set(prev).add(sessionId));
    try {
      await sessionService.deleteSession(String(sessionId));
      fetchSessions(currentPage);
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete session. Please try again.");
    } finally {
      setDeletingSessions((prev) => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  };

  const openSessionLog = async (session: Session) => {
    setViewingLogSession(session);
    setIsLogModalOpen(true);
    setLoadingLog(true);
    try {
      const data = await sessionService.getSessionLog(String(session.id));
      setSessionLogMessages(data.log || []);
    } catch (err) {
      console.error("Failed to fetch session log:", err);
      setSessionLogMessages([]);
    } finally {
      setLoadingLog(false);
    }
  };

  // Determine campaign ownership by comparing numeric user IDs (reliable even if createdBy string is empty)
  useEffect(() => {
    if (currentUserId && campaign.createdByUserId) {
      setIsCampaignOwner(currentUserId === campaign.createdByUserId);
    } else {
      // Fallback: string comparison for backward compatibility
      const username = auth.get("username");
      setIsCampaignOwner(!!username && username === campaign.createdBy);
    }
  }, [campaign, currentUserId, auth]);

  // Get current user ID from JWT token
  useEffect(() => {
    const userId = parseInt(auth.get("userId")!);
    setCurrentUserId(userId);
  }, [auth]);

  useEffect(() => {
    fetchSessions(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, campaign.id]);

  // Fetch user characters when currentUserId is available
  useEffect(() => {
    if (currentUserId) {
      fetchUserCharacters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, campaign.id]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Define table columns
  const columns: Column<Session>[] = [
    {
      key: "id",
      header: "Session #",
      sortable: true,
      render: (session) => `#${session.id}`,
    },
    {
      key: "dmName",
      header: "Dungeon Master",
      sortable: true,
      render: (session) => (
        <div>
          <div className="font-medium text-white">{session.dmName}</div>
          <div className="text-sm text-zinc-400">@{session.dmUsername}</div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (session) => {
        const statusColors: Record<string, string> = {
          created: "bg-amber-900/30 text-amber-300 border border-amber-800",
          active: "bg-emerald-900/30 text-emerald-300 border border-emerald-800",
          paused: "bg-zinc-800 text-zinc-400 border border-zinc-700",
          ended: "bg-red-900/20 text-red-400 border border-red-900",
        };
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 text-xs tracking-wide ${
              statusColors[session.status] || statusColors.created
            }`}
          >
            {session.status || "created"}
          </span>
        );
      },
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      render: (session) => new Date(session.createdAt).toLocaleDateString(),
    },
    {
      key: "updatedAt",
      header: "Last Updated",
      sortable: true,
      render: (session) => {
        const date = new Date(session.updatedAt);
        return (
          date.toLocaleDateString() +
          " " +
          date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        );
      },
    },
    {
      key: "sessionLog",
      header: "Session Log",
      render: (session) => (
        <div className="max-w-xs truncate">
          {session.status === "ended" ? (
            <Button size="sm" variant="secondary" onClick={() => openSessionLog(session)}>
              View Log
            </Button>
          ) : (
            <span className="text-zinc-500 italic">
              {session.status === "active" ? "In progress..." : "Not started"}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (session) => (
        <div className="flex flex-wrap gap-2">
          {session.status === "created" &&
            (session.dmPlayerId === currentUserId || isCampaignOwner) && (
              <Button
                size="sm"
                variant="primary"
                onClick={() => startSession(session.id)}
                disabled={startingSessions.has(session.id)}
              >
                {startingSessions.has(session.id) ? "Starting..." : "Start"}
              </Button>
            )}
          {session.status === "active" && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => openJoinModal(session)}
              disabled={joiningSessionInProgress}
            >
              {joiningSessionInProgress ? "Joining..." : "Join"}
            </Button>
          )}
          {session.status === "active" &&
            (session.dmPlayerId === currentUserId || isCampaignOwner) && (
              <Button
                size="sm"
                variant="danger"
                onClick={() => endSession(session.id)}
                disabled={endingSessions.has(session.id)}
              >
                {endingSessions.has(session.id) ? "Ending..." : "End"}
              </Button>
            )}
          {(session.status === "created" || session.status === "ended") && isCampaignOwner && (
            <Button
              size="sm"
              variant="danger"
              onClick={() => deleteSession(session.id)}
              disabled={deletingSessions.has(session.id)}
            >
              {deletingSessions.has(session.id) ? "Deleting..." : "Delete"}
            </Button>
          )}
          {session.status === "paused" && <span className="text-sm text-zinc-500">Paused</span>}
        </div>
      ),
    },
  ];

  // Prepare DM options for the dropdown
  const dmOptions = [
    { value: "", label: "Select a Dungeon Master" },
    ...campaign.users.map((user) => ({
      value: user.id.toString(),
      label: `${user.name} (@${user.username})`,
    })),
  ];

  return (
    <div className="space-y-6">
      {/* Header with Create Session Button */}
      <div className="flex items-center justify-between pb-4 mb-2 border-b border-zinc-800">
        <div>
          <h3 className="text-sm font-semibold text-zinc-200">Campaign Sessions</h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            {total > 0 ? `${total} session${total !== 1 ? "s" : ""} found` : "No sessions yet"}
          </p>
        </div>
        {isCampaignOwner && (
          <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
            Create Session
          </Button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-900/20 border border-red-800 text-red-300 px-4 py-3 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-300 hover:text-white ml-4">
            ×
          </button>
        </div>
      )}

      {/* Sessions Table */}
      {loading ? (
        <LoadingState message="Loading sessions..." size="lg" variant="spinner" />
      ) : sessions.length === 0 ? (
        <EmptyState
          title="No Sessions Found"
          description={
            isCampaignOwner
              ? "You haven't created any sessions yet. Create your first session to get started!"
              : "This campaign doesn't have any sessions yet."
          }
          icon="info"
          size="lg"
          action={
            isCampaignOwner ? (
              <Button onClick={() => setIsCreateModalOpen(true)} variant="primary">
                Create First Session
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="bg-zinc-900 border border-zinc-800">
          <DataTable<Session>
            data={sessions}
            columns={columns}
            onPageChange={handlePageChange}
            currentPage={currentPage}
            totalPages={Math.ceil(total / pageSize)}
            totalItems={total}
            pageSize={pageSize}
          />
        </div>
      )}

      {/* Create Session Modal */}
      {isCreateModalOpen && (
        <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} size="md">
          <ModalHeader title="Create New Session" onClose={() => setIsCreateModalOpen(false)} />
          <ModalBody>
            <div className="space-y-4">
              <p className="text-zinc-300">
                Select a Dungeon Master for this session. The DM will be responsible for running the
                session.
              </p>
              <Select
                label="Dungeon Master"
                value={selectedDMId?.toString() || ""}
                onChange={(e) => setSelectedDMId(e.target.value ? parseInt(e.target.value) : null)}
                options={dmOptions}
                fullWidth
                required
              />
              <Select
                label="Session Theme"
                value={selectedTheme}
                onChange={(e) => setSelectedTheme(e.target.value)}
                options={getAvailableThemes().map((theme) => ({
                  value: theme.id,
                  label: theme.name + " - " + theme.description,
                }))}
                fullWidth
                required
              />
              {campaign.users.length === 0 && (
                <div className="bg-amber-900/20 border border-amber-800 text-amber-300 px-4 py-3">
                  No users are currently in this campaign. Invite users to the campaign first.
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={() => setIsCreateModalOpen(false)}
              variant="secondary"
              size="md"
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              onClick={createSession}
              variant="primary"
              size="md"
              disabled={!selectedDMId || creating}
            >
              {creating ? "Creating..." : "Create Session"}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Join Session Modal */}
      {isJoinModalOpen && (
        <Modal isOpen={isJoinModalOpen} onClose={() => setIsJoinModalOpen(false)} size="md">
          <ModalHeader title="Join Session" onClose={() => setIsJoinModalOpen(false)} />
          <ModalBody>
            <div className="space-y-4">
              <p className="text-zinc-300">Select a character to join the session with.</p>
              <Select
                label="Character"
                value={selectedCharacterId || ""}
                onChange={(e) => setSelectedCharacterId(e.target.value || null)}
                options={[
                  { value: "", label: "Select a character" },
                  ...userCharacters.map((char) => ({
                    value: char.id.toString(),
                    label: char.name,
                  })),
                ]}
                fullWidth
                required
              />
              <div className="bg-zinc-800 border border-zinc-700 px-4 py-3">
                <p className="text-sm text-zinc-300">
                  Once you join the session, you'll be redirected to the session page where you can
                  interact with other players and the DM.
                </p>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setIsJoinModalOpen(false)} variant="secondary" size="md">
              Cancel
            </Button>
            <Button
              onClick={joinSession}
              variant="primary"
              size="md"
              disabled={!selectedCharacterId || joiningSessionInProgress}
            >
              {joiningSessionInProgress ? "Joining..." : "Join Session"}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Session Log Modal */}
      {isLogModalOpen && viewingLogSession && (
        <Modal
          isOpen={isLogModalOpen}
          onClose={() => {
            setIsLogModalOpen(false);
            setViewingLogSession(null);
            setSessionLogMessages([]);
          }}
          size="lg"
        >
          <ModalHeader
            title={`Session #${viewingLogSession.id} Log`}
            onClose={() => {
              setIsLogModalOpen(false);
              setViewingLogSession(null);
              setSessionLogMessages([]);
            }}
          />
          <ModalBody>
            <div className="space-y-4">
              <div className="flex gap-4 text-sm text-zinc-400">
                <span>
                  DM: <span className="text-white">{viewingLogSession.dmName}</span>
                </span>
                {viewingLogSession.endedAt && (
                  <span>
                    Ended:{" "}
                    <span className="text-white">
                      {new Date(viewingLogSession.endedAt).toLocaleString()}
                    </span>
                  </span>
                )}
              </div>
              {loadingLog ? (
                <LoadingState message="Loading session log..." size="md" variant="spinner" />
              ) : sessionLogMessages.length === 0 ? (
                <EmptyState
                  title="No Log Entries"
                  description="This session has no recorded log entries."
                  icon="info"
                  size="md"
                />
              ) : (
                <div className="max-h-96 overflow-y-auto space-y-2 pr-2">
                  {[...sessionLogMessages].reverse().map((msg) => (
                    <div
                      key={msg.id}
                      className={`px-3 py-2 rounded text-sm ${
                        msg.message_type === "dice_roll"
                          ? "bg-amber-900/20 border border-amber-800/40 text-amber-300"
                          : "bg-zinc-800 border border-zinc-700 text-zinc-200"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-white">
                          {msg.character_name || msg.sender_username}
                        </span>
                        <span className="text-xs text-zinc-500">
                          {new Date(msg.sent_at).toLocaleTimeString()}
                        </span>
                        {msg.message_type === "dice_roll" && (
                          <span className="text-xs bg-amber-800/50 text-amber-300 px-1.5 py-0.5 rounded">
                            🎲 Dice Roll
                          </span>
                        )}
                      </div>
                      <p className="text-zinc-300">{msg.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={() => {
                setIsLogModalOpen(false);
                setViewingLogSession(null);
                setSessionLogMessages([]);
              }}
              variant="secondary"
              size="md"
            >
              Close
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};

export default CampaignSessions;
